import { useRouterState } from "@tanstack/solid-router";
import { createEffect, createSignal, onCleanup } from "solid-js";

/**
 * A slim progress bar fixed to the top of the viewport that animates during
 * page transitions. Inspired by YouTube's loading bar.
 *
 * Uses the router's `isLoading` flag to know when a navigation starts and
 * finishes.  While loading, the bar crawls from 0 % to ~90 %; on completion
 * it snaps to 100 % briefly then fades out.
 */
export default function NavigationProgress() {
  const routerState = useRouterState();

  const [visible, setVisible] = createSignal(false);
  const [progress, setProgress] = createSignal(0);

  let rafId: number | undefined;
  let startedAt = 0;
  // Guards against the router state re-evaluating mid-navigation. The router
  // state object is recreated whenever any of its fields change, so the effect
  // below can re-run while `isLoading` is still true. Without this flag each
  // re-run would reset the bar back to 0 and it would never reach full width.
  let running = false;

  // Progress curve: fast start (~30% in 300 ms), then slow crawl toward 90%.
  // This mimics a realistic navigation: most of the uncertainty is early on,
  // and we never quite reach 100% until the route actually resolves.
  const DURATION_MS = 5_000; // time to crawl from 0 → ~90 %
  const MAX_CRAWL = 0.9;

  function startProgress() {
    if (running) return;
    running = true;
    startedAt = performance.now();
    setVisible(true);
    setProgress(0);

    function tick(now: number) {
      const elapsed = now - startedAt;
      const t = Math.min(elapsed / DURATION_MS, 1);
      // Ease-out cubic so it decelerates as it goes.
      const eased = 1 - Math.pow(1 - t, 3);
      setProgress(eased * MAX_CRAWL);

      if (t < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        // Keep holding at ~90% until the route finishes.
        rafId = requestAnimationFrame(() => setProgress(MAX_CRAWL));
      }
    }

    rafId = requestAnimationFrame(tick);
  }

  function completeProgress() {
    if (!running) return;
    running = false;
    if (rafId !== undefined) {
      cancelAnimationFrame(rafId);
      rafId = undefined;
    }
    // Snap to 100 %
    setProgress(1);

    // Brief pause so the user sees the full bar, then hide.
    const timeout = setTimeout(() => {
      setVisible(false);
      // Reset after the fade-out so it starts fresh next time.
      setTimeout(() => setProgress(0), 200);
    }, 300);

    onCleanup(() => clearTimeout(timeout));
  }

  createEffect(() => {
    const state = routerState();
    if (state.isLoading) {
      startProgress();
    } else if (visible()) {
      completeProgress();
    }
  });

  return (
    <div
      role="progressbar"
      aria-label="Page loading"
      aria-valuenow={Math.round(progress() * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      class="navigation-progress"
      classList={{ "navigation-progress--visible": visible() }}
    >
      <div
        class="navigation-progress__bar"
        style={{
          transform: `scaleX(${progress()})`,
        }}
      >
        <div class="navigation-progress__glow" />
      </div>
    </div>
  );
}
